import { useCallback, useMemo, useState } from "react";
import { shuffleArray } from "../../utils/shuffleArray";
import { getRandomArrayItem } from "../../utils/getRandomArrayItem";
import { sleep } from "../../utils/sleep";
import "./styles.css";

const slotMachineImage = "/images/slot-machine-image.png";
const slotMachineAnimation = "/images/slot-machine-animation.gif";

const fixArrayLength = (array) => {
  const arrayExpectedSize = 8;

  if (array.length >= arrayExpectedSize) {
    return array.slice(0, arrayExpectedSize);
  } else {
    const arrayWithExpectedSize = [...array];

    while (arrayWithExpectedSize.length < 8) {
      const diffLength = 8 - array.length;

      for (let i = 0; i < Math.min(diffLength, array.length); i++) {
        arrayWithExpectedSize.push(array[i]);
      }
    }

    return arrayWithExpectedSize;
  }
};

function SlotMachine({ result, items, initialItems, canPlay, onWin }) {
  const itemsFixed = useMemo(() => {
    if (!items || !items.length) return [];

    return fixArrayLength(items);
  }, [items]);

  const slotsItems = useMemo(() => {
    const slot1Items = shuffleArray(itemsFixed);
    const slot2Items = shuffleArray(itemsFixed);
    const slot3Items = shuffleArray(itemsFixed);

    return [
      [...slot1Items, ...slot1Items],
      [...slot2Items, ...slot2Items],
      [...slot3Items, ...slot3Items],
    ];
  }, [itemsFixed]);

  const [running, setRunning] = useState(false);
  const [slotsRunning, setSlotsRunnig] = useState([false, false, false]);
  const [machineImage, setMachineImage] = useState(slotMachineImage);
  const [slotsImages, setSlotsImages] = useState(
    initialItems ? initialItems : [slotsItems[0][0], slotsItems[1][0], slotsItems[2][0]],
  );

  const stopAll = useCallback(async (resultSlots) => {
    setSlotsImages(resultSlots);

    setSlotsRunnig([false, true, true]);

    await sleep(300);

    setSlotsRunnig([false, false, true]);

    await sleep(300);

    setSlotsRunnig([false, false, false]);
  }, []);

  const run = useCallback(async () => {
    setRunning(true);

    if (canPlay) {
      const canPlayResult = await canPlay();

      if (!canPlayResult) {
        setRunning(false);
        return;
      }
    }

    setSlotsRunnig([true, true, true]);
    setMachineImage(slotMachineAnimation);

    setTimeout(async () => {
      setMachineImage(slotMachineImage);

      if (result) {
        try {
          const [, resultData] = await Promise.all([sleep(2600), result()]);

          const { icons, win, reward } = resultData;

          await stopAll(icons);

          if (onWin && win) {
            await onWin(reward);
          }
        } catch {
          await stopAll([]);
        }
      } else {
        await sleep(2600);

        await stopAll([
          getRandomArrayItem(slotsItems[0]),
          getRandomArrayItem(slotsItems[1]),
          getRandomArrayItem(slotsItems[2]),
        ]);
      }

      setRunning(false);
    }, 600);
  }, [canPlay, onWin, result, slotsItems, stopAll]);

  return (
    <div className="slot-machine-container">
      <img className="preloadImage" src={slotMachineImage} alt="" />
      <img className="preloadImage" src={slotMachineAnimation} alt="" />
      <div
        className={`${!running ? "cursor-pointer" : ""}`}
        id="slot-machine-trigger"
        onClick={!running ? run : undefined}
      />

      <img id="machine" src={machineImage} alt="Slot Machine" />
      <div className="items-container">
        <div className="items-slot">
          <div className={`slot ${slotsRunning[0] ? "items" : "items-stoped"}`}>
            {slotsRunning[0] ? (
              <>
                {slotsItems[0].map((currentItem, index) => (
                  <img key={index} src={currentItem} alt="" />
                ))}
              </>
            ) : (
              <img src={slotsImages[0]} alt="" />
            )}
          </div>
        </div>
        <div className="items-slot">
          <div className={`slot ${slotsRunning[1] ? "items" : "items-stoped"}`}>
            {slotsRunning[1] ? (
              <>
                {slotsItems[1].map((currentItem, index) => (
                  <img key={index} src={currentItem} alt="" />
                ))}
              </>
            ) : (
              <img src={slotsImages[1]} alt="" />
            )}
          </div>
        </div>
        <div className="items-slot">
          <div className={`slot ${slotsRunning[2] ? "items" : "items-stoped"}`}>
            {slotsRunning[2] ? (
              <>
                {slotsItems[2].map((currentItem, index) => (
                  <img key={index} src={currentItem} alt="" />
                ))}
              </>
            ) : (
              <img src={slotsImages[2]} alt="" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SlotMachine;
